/* eslint-disable eqeqeq */
import { Button, Group, Loader, Modal, Select, SimpleGrid, Space, Stack, TextInput, } from "@mantine/core";
import { notifications } from '@mantine/notifications';
import { useFirestore, useFunctions } from "../helpers/firebaseContext";
import { collection, doc, getDoc, query } from "firebase/firestore";
import { useForm } from "@mantine/form";
import useFirestoreCollection from "../helpers/useFirestoreCollection";
import { useContext, useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { CustomClaims, Customer, CustomerNotification, PromotionalMaterial, RMUser } from "../types";
import { MyUserDataContext } from "../contexts";
import MobileCodeSelect, { dialCodeForSegment, isValidLocalMobile, splitMobileWithDialCode } from "./mobile_code_select";
import { isPromotionalMaterialRestricted } from "../../../shared/promotionalMaterialAccess";
import { useCanSendPromotionalMaterial } from "../helpers/useCanSendPromotionalMaterial";


interface SendPromotionalMaterial {
  opened: boolean,
  setOpened: (value: boolean) => void,
  customerData?: Customer,
}
const SendPromotionalMaterial = ({ opened, setOpened, customerData }: SendPromotionalMaterial) => {



  const db = useFirestore();
  const promotionalMaterialsCollection = collection(db, "product_materials")
  const promotionalMaterialsCollectionQuery = query(promotionalMaterialsCollection,);

  const [materialsList, setMaterialsList] = useState<any[]>([])
  const [selectedMaterial, setSelectedMaterial] = useState<any>()
  const { loading, snapshot: product_materials } = useFirestoreCollection(promotionalMaterialsCollectionQuery);
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const userContext = useContext(MyUserDataContext);
  const canSendPromotionalMaterial = useCanSendPromotionalMaterial();

  let userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};

  const functions = useFunctions();
  const sendGenericWhatsappMessageCallable = httpsCallable<{
    queryString?: string,
    collectionName?: "rms" | "customers",
    whatsappTemplateID: string,
    userVariables?: Record<string, any>,
    notificationData?: Partial<CustomerNotification>,
    directRecipient?: {
      mobile: string
      /** dial code for `mobile` ("+965", "+20"); defaults to the RM's segment country on the server */
      mobile_code?: string
      name_en: string
      name_ar: string
    }
  }>(functions, "sendGenericWhatsappMessageNotification");
  // Typed numbers default to the RM's country: "+20" for the KFH Egypt segment, "+965" otherwise.
  const defaultDialCode = dialCodeForSegment(userClaims.segment);
  // Debug logging removed: console.log("material=================>>", product_materials)

  useEffect(() => {
    if (opened) {
      if (product_materials) {

        if (product_materials?.empty) {
          notifications.show({
            title: 'no promotional material',
            message: 'no promotional material',
            color: 'yellow',
          });
        } else {
          let materialList = (product_materials?.docs?.map((docObj) => {
            const materialData = docObj.data() as PromotionalMaterial;
            return {
              ...materialData,
              "label": `${docObj.data().title}`,
              "value": docObj.id
            }
          }) || [])
          if (userClaims["kfh_rm"] || userClaims["kfh_oper"]) {
            materialList = materialList.filter((materialOption) => materialOption.state === "ready_for_investors")
          }
          if (userClaims["cr"]) {
            materialList = materialList.filter((materialOption) => ["internal_only", "ready_for_investors"].includes(materialOption?.state ?? ""))
          }
          setMaterialsList(materialList)

        }
      }
    }
  }, [opened, product_materials])


  const form = useForm({
    initialValues: {
      material: "",
      mobile: "",
      mobile_code: defaultDialCode,
      name_en: "",
      name_ar: "",
    },
    validate: {
      material: (value) => !value ? "Please select material" : null,
      mobile: (value, values) => {
        if (customerData) return null;
        if (!value) return "Mobile is required";
        const parts = splitMobileWithDialCode(value, values.mobile_code || defaultDialCode);
        return isValidLocalMobile(parts.local, parts.dialCode) ? null : "Enter the local number digits only (the country code is selected separately)";
      },
      name_en: (value) => !customerData && !value ? "English name is required" : null,
      name_ar: (value) => !customerData && !value ? "Arabic name is required" : null,
    }
  });

  useEffect(() => {
    const foundDocument = product_materials?.docs.find((docObj) => docObj.id === form.values?.material);
    setSelectedMaterial(foundDocument?.data() ?? undefined);
  }, [form.values.material])

  if (loading) {
    return <Loader />
  }
  return (
    <>
      <Modal
        opened={opened && canSendPromotionalMaterial}
        onClose={() => {

          setOpened(false);

        }}
        title="Send Material"
        size="lg"
      >
        <form
          onSubmit={form.onSubmit(async (values) => {

            try {
              if (!canSendPromotionalMaterial) {
                throw Error("You are not permitted to send promotional material.");
              }
              setLoadingSubmit(true);

              const selectedMaterial = product_materials?.docs.find((docObj) => docObj.id === values.material)
              if (!selectedMaterial) {
                notifications.show({
                  color: "red",
                  message: "failed to find material"
                })
                setLoadingSubmit(false);
                return;
              }
              let rmData: RMUser | undefined = undefined;
              if (userContext.user?.email) {

                const rmRef = doc(db, "rms", userContext.user?.email)
                const rmDoc = await getDoc(rmRef);
                if (rmDoc.exists()) {
                  rmData = rmDoc.data() as RMUser;
                }
              }
              if (!rmData?.rm_id) {
                throw Error("RM User Not found");
              }
              if (isPromotionalMaterialRestricted(userClaims, rmData)) {
                throw Error("Uncertified Private Banking RMs cannot send promotional material.");
              }
              const selectedMaterialData = selectedMaterial?.data() as PromotionalMaterial;
              const mediaID = selectedMaterialData.file?.split("https://storage.googleapis.com/kfh-capital-crm.appspot.com/portal-uploads/").pop()

              // stored shape: local digits + dial code (a "+20..." typed number is split here)
              const typedMobile = splitMobileWithDialCode(values.mobile, values.mobile_code || defaultDialCode);
              let userVariables: any = {
                ...(customerData || {
                  mobile: typedMobile.local,
                  mobile_code: typedMobile.dialCode,
                  name_en: values.name_en,
                  name_ar: values.name_ar,
                }),
                rmID: rmData?.rm_id,
                crmID: customerData?.crmID || "KFHC",
                product_name_ar: selectedMaterialData.name_ar,
                product_name_en: selectedMaterialData.name_en,
                product_reference: selectedMaterialData?.reference,
                // mediaID: mediaID
              };
              if (mediaID) {
                userVariables["mediaID"] = mediaID
              }


              if (customerData?.id) {
                await sendGenericWhatsappMessageCallable({
                  queryString: `documentID:["${customerData?.id}"]`,
                  collectionName: "customers",
                  whatsappTemplateID: selectedMaterialData.whatsappTemplateID,
                  userVariables,
                  notificationData: {
                    promotional_material: selectedMaterialData,
                    title: `promotion:${selectedMaterialData.title} ${selectedMaterialData.name_en} - ${selectedMaterialData.name_ar}`,
                    notification_type: "marketing"
                  }
                })
              } else {
                await sendGenericWhatsappMessageCallable({
                  directRecipient: {
                    mobile: typedMobile.local,
                    mobile_code: typedMobile.dialCode,
                    name_en: values.name_en,
                    name_ar: values.name_ar,
                  },
                  notificationData: {
                    promotional_material: selectedMaterialData,
                    title: `promotion: ${selectedMaterialData.title} ${selectedMaterialData.name_en} - ${selectedMaterialData.name_ar}`,
                    notification_type: "marketing"
                  },
                  whatsappTemplateID: selectedMaterialData.whatsappTemplateID,
                  userVariables,
                })
              }
              setLoadingSubmit(false);
              setOpened(false);
              notifications.show({
                color: "green",
                message: "promotional material sent"
              })
            } catch (error: any) {
              setLoadingSubmit(false);
              console.log("error ------>", error);

              notifications.show({
                color: "red",
                message: "an error occured"
              })
            }

          })}
        >
          <Stack gap="sm">
            {!!materialsList?.length && <Select
              label="Material"
              placeholder="Select the material to send"
              withAsterisk
              data={materialsList}
              searchable
              error={form.errors.material}
              onChange={(val) => {
                if (val) {
                  form.setFieldValue("material", val)
                }
              }
              }
            />}

            {!customerData && <>
              <Group align="start" wrap="nowrap" gap="xs">
                <MobileCodeSelect
                  style={{ flex: "0 0 45%" }}
                  value={form.values.mobile_code || defaultDialCode}
                  onChange={(dialCode) => form.setFieldValue("mobile_code", dialCode)}
                />
                <TextInput
                  style={{ flex: 1 }}
                  withAsterisk
                  label="Mobile"
                  placeholder={(form.values.mobile_code || defaultDialCode) === "+965" ? "9XXXXXXX" : "Local number"}
                  {...form.getInputProps("mobile")}
                  onChange={(event) => {
                    // a full "+20..." number typed here moves its country code into the picker
                    const typed = event.currentTarget.value;
                    const parts = splitMobileWithDialCode(typed, form.values.mobile_code || defaultDialCode);
                    if (parts.matched) form.setFieldValue("mobile_code", parts.dialCode);
                    form.setFieldValue("mobile", parts.matched ? parts.local : typed);
                  }}
                />
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                <TextInput
                  withAsterisk
                  label="Name (EN)"
                  placeholder="Customer name in English"
                  {...form.getInputProps("name_en")}
                />
                <TextInput
                  withAsterisk
                  label="Name (AR)"
                  placeholder="اسم العميل"
                  {...form.getInputProps("name_ar")}
                />
              </SimpleGrid>
            </>}
          </Stack>

          <Space h={"md"} />

          <Group>

            {selectedMaterial && selectedMaterial.file && <Button component="a" target="_blank" href={
              selectedMaterial?.file
            }>

              View Selected file
            </Button>}

            <Button
              loading={loadingSubmit}
              type="submit"

            >
              Send
            </Button>
          </Group>
        </form>
      </Modal >
    </>
  );
}

export default SendPromotionalMaterial;
